import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import axios from 'axios';
import { powerAtom, Power } from '../atoms/power';
import { BuildingIDProps } from '../atoms/chargers';
import PowerGraph from '../charts/PowerGraph';
import VoltageGraph from '../charts/VoltageGraph';
import { DateTime } from 'luxon';
import { Card, CardContent, Box } from '@mui/material';
import DownloadButton from '../buttons/DownloadButton';
import GraphTimePicker from '../buttons/GraphTimePicker';
import useWebSocket from '../hooks/wsHook';
import { fpmEndpointLink, fpmWebsocketLink } from '../../../index';
import { sharedGraphTimeRangeAtom, type GraphRangeLabel } from '../atoms/graphTimeRange';

/**
 * Power graph + voltage graph driven by FPM endpoints.
 *
 * This is the read-only port of FPM's PowerGraphHandler; the demand-limit
 * Apply form is intentionally omitted (GolfTrack users don't operate the
 * facility's power controls).
 */
const PowerGraphHandler: React.FC<BuildingIDProps> = ({ BuildingID }) => {
  const [powerData, setPowerData] = useAtom(powerAtom);
  const [sharedTimeRange, setSharedTimeRange] = useAtom(sharedGraphTimeRangeAtom);
  const startDateTime = DateTime.fromISO(sharedTimeRange.startISO, { zone: 'America/New_York' });
  const endDateTime = DateTime.fromISO(sharedTimeRange.endISO, { zone: 'America/New_York' });
  const selectedRange = sharedTimeRange.selectedRange;
  const setStartDateTime = (date: DateTime) => {
    setSharedTimeRange((prev) => ({
      ...prev,
      startISO: date.setZone('America/New_York').toISO() ?? prev.startISO,
    }));
  };
  const setEndDateTime = (date: DateTime) => {
    setSharedTimeRange((prev) => ({
      ...prev,
      endISO: date.setZone('America/New_York').toISO() ?? prev.endISO,
    }));
  };
  const setSelectedRange = (label: string) => {
    setSharedTimeRange((prev) => ({
      ...prev,
      selectedRange: label as GraphRangeLabel,
    }));
  };
  const [suppressUntilFetch, setSuppressUntilFetch] = useState<boolean>(false);
  const { messages } = useWebSocket(fpmWebsocketLink, ['power']);

  const lastProcessedIndexRef = useRef(-1);
  const prevStartRef = useRef<DateTime>(startDateTime);
  const prevEndRef = useRef<DateTime>(endDateTime);

  useEffect(() => {
    const buildingKey = BuildingID?.toLowerCase() || '';
    const hasRangeChanged =
      prevStartRef.current.toMillis() !== startDateTime.toMillis() ||
      prevEndRef.current.toMillis() !== endDateTime.toMillis();

    prevStartRef.current = startDateTime;
    prevEndRef.current = endDateTime;

    if (!hasRangeChanged) return;

    if (selectedRange === 'Custom') {
      setSuppressUntilFetch(true);
      setPowerData((prev) => ({
        ...prev,
        [buildingKey]: [],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDateTime, endDateTime, BuildingID, selectedRange, setPowerData]);

  useEffect(() => {
    if (messages.length === 0) return;

    const newMessages = messages.slice(lastProcessedIndexRef.current + 1);
    lastProcessedIndexRef.current = messages.length - 1;

    newMessages.forEach((messageObj) => {
      const { channel, message } = messageObj;

      try {
        if (channel === 'power' && message && selectedRange !== 'Custom') {
          const powerMessage = message as Power;
          if (powerMessage.BuildingID?.toLowerCase() === BuildingID?.toLowerCase()) {
            const convertedMessage = {
              ...powerMessage,
              TimestampMs: powerMessage.Timestamp
                ? DateTime.fromISO(powerMessage.Timestamp, { zone: 'utc' }).toMillis()
                : DateTime.utc().toMillis(),
            };

            setPowerData((prev) => {
              const buildingID = BuildingID!.toLowerCase();
              const existingData = prev[buildingID] || [];

              const timestampExists = existingData.some(data => data.TimestampMs === convertedMessage.TimestampMs);
              if (timestampExists) return prev;

              const newData = [...existingData, convertedMessage];
              return { ...prev, [buildingID]: newData };
            });
          }
        }
      } catch (error) {
        console.error('Failed to process power message:', error);
      }
    });
  }, [messages, BuildingID, selectedRange, setPowerData]);

  useEffect(() => {
    lastProcessedIndexRef.current = -1;
  }, [BuildingID, selectedRange]);

  const { isFetching } = useQuery({
    queryKey: ['fpmPowerGraph', BuildingID, sharedTimeRange.startISO, sharedTimeRange.endISO],
    staleTime: 0,
    enabled: !!BuildingID && startDateTime.isValid && endDateTime.isValid,
    queryFn: async ({ signal }: { signal?: AbortSignal }) => {
      const buildingKey = BuildingID?.toLowerCase() || '';
      try {
        const response = await axios.get<Power[]>(`${fpmEndpointLink}power-graph`, {
          params: {
            BuildingID: BuildingID,
            start: startDateTime.toISO(),
            end: endDateTime.toISO(),
          },
          signal,
        });

        const data = response.data;
        updatePowerData(data);
        return data;
      } catch (error) {
        if (
          axios.isCancel(error) ||
          (typeof error === 'object' &&
            error !== null &&
            ('code' in error || 'name' in error) &&
            ((error as { code?: string }).code === 'ERR_CANCELED' ||
              (error as { name?: string }).name === 'CanceledError'))
        ) {
          return powerData[buildingKey] || [];
        }
        console.error('[PowerGraph] FPM query failed:', error);
        throw error;
      } finally {
        setSuppressUntilFetch(false);
      }
    },
  });

  function updatePowerData(data: Power[]) {
    const buildingKey = BuildingID!.toLowerCase();

    if (!data || data.length === 0) {
      setPowerData((prev) => ({ ...prev, [buildingKey]: [] }));
      return;
    }

    let filteredData = data.filter(p => p.BuildingID && p.BuildingID.toLowerCase() === buildingKey);

    if (selectedRange === 'Custom') {
      const startMs = startDateTime.toMillis();
      const endMs = endDateTime.toMillis();
      filteredData = filteredData.filter(d => {
        const ms = d.TimestampMs ?? (d.Timestamp ? new Date(d.Timestamp.replace(' ', 'T') + 'Z').getTime() : 0);
        return ms >= startMs && ms <= endMs;
      });
    }

    if (selectedRange === 'Custom' && filteredData.length === 0) {
      setPowerData((prev) => ({ ...prev, [buildingKey]: [] }));
      return;
    }

    setPowerData((prev) => ({ ...prev, [buildingKey]: filteredData }));
  }

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
      <CardContent sx={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0, padding: '16px', gap: 2 }}>
        <Box sx={{ flex: '1 1 0', minHeight: 360 }}>
          <PowerGraph
            BuildingID={BuildingID}
            startDateTime={startDateTime}
            endDateTime={endDateTime}
            isLoading={isFetching || suppressUntilFetch}
            forceNoDataOnEmpty={selectedRange === 'Custom'}
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <GraphTimePicker
            startDateTime={startDateTime}
            endDateTime={endDateTime}
            setStartDateTime={setStartDateTime}
            setEndDateTime={setEndDateTime}
            selectedRange={selectedRange}
            setSelectedRange={setSelectedRange}
            isLoading={isFetching || suppressUntilFetch}
          />
          <DownloadButton
            data={(powerData[BuildingID?.toLowerCase() || ''] || []).map((item: Power) => ({
              TimestampMs: item.TimestampMs ?? (item.Timestamp ? new Date(item.Timestamp.replace(' ', 'T') + 'Z').getTime() : 0),
              Grid: item.Grid,
              Load: item.Load,
              Solar: item.Solar,
              Demand: item.Demand,
              DayPeak: item.DayPeak,
            }))}
            filename={`${BuildingID}_power_${startDateTime.toFormat('yyyy-MM-dd')}_to_${endDateTime.toFormat('yyyy-MM-dd')}`}
            timestampField="TimestampMs"
            valueFields={["Grid", "Load", "Solar", "Demand", "DayPeak"]}
            disabled={!powerData[BuildingID?.toLowerCase() || '']?.length}
            timezone="America/New_York"
          />
        </Box>
        <Box sx={{ flex: '1 1 0', minHeight: 360 }}>
          <VoltageGraph
            BuildingID={BuildingID}
            startDateTime={startDateTime}
            endDateTime={endDateTime}
            isLoading={isFetching || suppressUntilFetch}
            forceNoDataOnEmpty={selectedRange === 'Custom'}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default PowerGraphHandler;
